import React, { useCallback, useEffect, useState } from "react";
import { ITrustedSecretaryAdminRow, ITrustedSecretaryClubRow } from "@eagerdog/interfaces";

import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import { Modal } from "../../../components/Modal/Modal";
import Button from "../../../components/Button/Button";

import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import styles from "./TrustedSecretariesTab.module.scss";

interface IProps {
  currentTab: number;
  tabIndex: number;
}

const currencyFormatter = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

const dateFormatter = (value: string | Date) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value));
};

interface IReferredClubsModalProps {
  secretary: ITrustedSecretaryAdminRow;
  onClose: () => void;
}

const ReferredClubsModal: React.FC<IReferredClubsModalProps> = ({ secretary, onClose }) => {
  const [clubs, setClubs] = useState<ITrustedSecretaryClubRow[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = themeQuartz;

  useEffect(() => {
    apiService.getAdminTrustedSecretaryClubs(secretary.user_id)
      .then(setClubs)
      .catch(() => {
        toast.show({ title: "Error", content: "Failed to load clubs", duration: 5000, type: "fail" });
      })
      .finally(() => setLoading(false));
  }, [secretary.user_id]);

  const clubColDefs: ColDef[] = [
    { headerName: "Club Name", field: "club_name", minWidth: 200, filter: true, valueFormatter: (p) => p.value || "—" },
    { headerName: "City", field: "city", minWidth: 120, filter: true, valueFormatter: (p) => p.value || "—" },
    { headerName: "State", field: "state", minWidth: 90, filter: true, valueFormatter: (p) => p.value || "—" },
    { headerName: "Country", field: "country", minWidth: 100, filter: true, valueFormatter: (p) => p.value || "—" },
    {
      headerName: "Referred On",
      field: "date_referred",
      minWidth: 130,
      filter: true,
      valueFormatter: (p) => dateFormatter(p.value),
    },
  ];

  const content = (
    <div className={styles.referredClubsWrap}>
      <h2 className={styles.referredClubsTitle}>Referred Clubs — {secretary.name}</h2>
      <div className={`ag-theme-quartz ${styles.referredClubsGrid}`}>
        <AgGridReact
          theme={theme}
          rowData={loading ? [] : clubs}
          columnDefs={clubColDefs}
          defaultColDef={{ resizable: true, sortable: true, flex: 1 }}
          domLayout="autoHeight"
          overlayLoadingTemplate="<span style='padding: 20px; font-size: 14px; color: #666;'>Loading…</span>"
          overlayNoRowsTemplate="<span style='padding: 20px; font-size: 14px; color: #666;'>No referred clubs found</span>"
          loading={loading}
        />
      </div>
      <div className={styles.referredClubsFooter}>
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );

  return (
    <Modal
      className={styles.referredClubsModal}
      isShown={true}
      hide={onClose}
      modalContent={content}
    />
  );
};

const TrustedSecretariesTab: React.FC<IProps> = ({ currentTab, tabIndex }) => {
  const [rowData, setRowData] = useState<ITrustedSecretaryAdminRow[]>([]);
  const [selectedSecretary, setSelectedSecretary] = useState<ITrustedSecretaryAdminRow | null>(null);
  const theme = themeQuartz;

  const loadReport = useCallback(async () => {
    try {
      const rows = await apiService.getAdminTrustedSecretariesReport();
      setRowData(rows);
    } catch (err) {
      console.error("Failed to load trusted secretaries report:", err);
      toast.show({
        title: "Error",
        content: "Failed to load trusted secretaries report",
        duration: 5000,
        type: "fail",
      });
    }
  }, []);

  useEffect(() => {
    if (currentTab === tabIndex) {
      loadReport();
    }
  }, [currentTab, tabIndex, loadReport]);

  const colDefs: ColDef[] = [
    {
      headerName: "Name",
      field: "name",
      minWidth: 180,
      filter: true,
      pinned: "left",
    },
    {
      headerName: "Email",
      field: "email",
      minWidth: 220,
      filter: true,
    },
    {
      headerName: "Referred Clubs",
      field: "clubs",
      minWidth: 150,
      filter: true,
      type: "numericColumn",
      cellRenderer: (params: any) => {
        const count = params.value ?? 0;
        if (count === 0) return <span>{count}</span>;
        return (
          <span
            className={styles.clubsLink}
            onClick={() => setSelectedSecretary(params.data)}
          >
            {count}
          </span>
        );
      },
    },
    {
      headerName: "Events",
      field: "events",
      minWidth: 120,
      filter: true,
      type: "numericColumn",
    },
    {
      headerName: "Total Fees Collected",
      field: "total_fees",
      minWidth: 190,
      filter: true,
      type: "numericColumn",
      valueFormatter: (p) => (p.value != null ? currencyFormatter(p.value) : "-"),
    },
    {
      headerName: "TTV",
      field: "ttv",
      minWidth: 150,
      filter: true,
      type: "numericColumn",
      valueFormatter: (p) => (p.value != null ? currencyFormatter(p.value) : "-"),
    },
    {
      headerName: "Commission (20%)",
      field: "commission",
      minWidth: 170,
      filter: true,
      type: "numericColumn",
      valueFormatter: (p) => (p.value != null ? currencyFormatter(p.value) : "-"),
    },
  ];

  if (currentTab !== tabIndex) return null;

  return (
    <div className={styles.TrustedSecretariesTab}>
      <div className={styles.tableWrap}>
        <div className={`ag-theme-quartz ${styles.grid}`}>
          <AgGridReact
            theme={theme}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={{ resizable: true, sortable: true }}
            overlayNoRowsTemplate="<span style='padding: 20px; font-size: 16px; color: #666;'>No trusted secretary accounts found</span>"
          />
        </div>
      </div>

      {selectedSecretary && (
        <ReferredClubsModal
          secretary={selectedSecretary}
          onClose={() => setSelectedSecretary(null)}
        />
      )}
    </div>
  );
};

export default TrustedSecretariesTab;
